import React from "react";
import { colors, fonts } from "@/theme";
import { InfoTip } from "@/components/InfoTip";
import { useStudio } from "@/store/useStudio";
import { computeFinancials } from "@/lib/financials";

const usd = (n) => `$${Math.round(n || 0).toLocaleString()}`;

// Phase 3 — localized CAPEX/OPEX roll-up (read-only summary). Per-instance CAPEX/OPEX
// overrides are now edited inside each Draft card and pushed here on "Done".
export default function FinancialsPanel() {
  const kb = useStudio((s) => s.kb);
  const systemBlocks = useStudio((s) => s.systemBlocks);
  const geo = useStudio((s) => s.geo);
  const overrides = useStudio((s) => s.financialOverrides);

  if (!systemBlocks?.length) return null;
  const fin = computeFinancials(systemBlocks, kb, geo?.name, overrides);

  return (
    <div style={st.wrap} data-testid="financials-panel">
      <div style={st.head}>
        <span style={{ ...st.title, display: "inline-flex", alignItems: "center", gap: 5 }}>
          FINANCIALS
          <InfoTip testId="financials-info" title="Financials" body="Edit CAPEX / OPEX per instance inside its Draft card." placement="bottom" />
        </span>
        <span style={st.mult}>localized ×{fin.mult}</span>
      </div>
      <div style={st.totals}>
        <div style={st.totalCard}><span style={st.totalLabel}>TOTAL CAPEX</span><span style={st.totalVal} data-testid="fin-total-capex">{usd(fin.totalCapex)}</span></div>
        <div style={st.totalCard}><span style={st.totalLabel}>ANNUAL OPEX</span><span style={st.totalVal} data-testid="fin-total-opex">{usd(fin.totalOpex)}/yr</span></div>
      </div>
      {fin.rows.map((r) => (
        <div key={r.id} style={st.row} data-testid={`fin-row-${r.id}`}>
          <div style={st.rowTop}>
            <span style={st.rowLabel}>{r.label}</span>
            <span style={st.rowArea}>{r.area.toLocaleString()} m²</span>
          </div>
          <div style={st.rateRow}>
            <span style={st.rate} data-testid={`fin-capex-${r.id}`}>{usd(r.capexRate)}/m² CAPEX</span>
            <span style={st.rate} data-testid={`fin-opex-${r.id}`}>{usd(r.opexRate)}/m²·yr OPEX</span>
          </div>
          <div style={st.rowTotals}>{usd(r.capex)} capex · {usd(r.opex)}/yr opex{r.overridden ? " · overridden in draft" : ""}</div>
        </div>
      ))}
    </div>
  );
}

const st = {
  wrap: { marginTop: 16, padding: 14, borderRadius: 14, background: "rgba(81,170,254,0.05)", border: "1px solid rgba(81,170,254,0.2)" },
  head: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 },
  title: { fontFamily: fonts.mono, fontSize: 10, letterSpacing: 1, fontWeight: 700, color: colors.brand },
  mult: { fontFamily: fonts.mono, fontSize: 9, color: colors.textTertiary },
  totals: { display: "flex", gap: 10, marginBottom: 12 },
  totalCard: { flex: 1, padding: "10px 12px", borderRadius: 10, background: "#fff", border: `1px solid ${colors.border}` },
  totalLabel: { display: "block", fontFamily: fonts.mono, fontSize: 8, letterSpacing: 1, color: colors.textTertiary, fontWeight: 700 },
  totalVal: { display: "block", fontFamily: fonts.sans, fontSize: 18, fontWeight: 800, color: colors.textPrimary, letterSpacing: -0.5, marginTop: 3 },
  row: { padding: "10px 0", borderTop: `1px solid ${colors.border}` },
  rowTop: { display: "flex", justifyContent: "space-between", marginBottom: 7 },
  rowLabel: { fontFamily: fonts.sans, fontSize: 13, fontWeight: 600, color: colors.textPrimary },
  rowArea: { fontFamily: fonts.mono, fontSize: 10.5, color: colors.textTertiary },
  overrideRow: { display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" },
  rateRow: { display: "flex", gap: 12, flexWrap: "wrap" },
  rate: { fontFamily: fonts.mono, fontSize: 10.5, color: colors.textSecondary, fontWeight: 600 },
  ovLabel: { fontFamily: fonts.mono, fontSize: 8.5, letterSpacing: 0.3, color: colors.textTertiary, fontWeight: 700 },
  ovInput: { width: 70, border: `1px solid ${colors.border}`, borderRadius: 8, padding: "5px 7px", fontFamily: fonts.mono, fontSize: 11.5, color: colors.textPrimary, background: "#fff" },
  rowTotals: { marginTop: 6, fontFamily: fonts.sans, fontSize: 11, color: colors.textSecondary },
  footHint: { marginTop: 10, fontFamily: fonts.sans, fontSize: 10.5, color: colors.textTertiary, fontStyle: "italic" },
};
